import { FmlxBackdropLoading, FmlxButton, FmlxCheckBox, FmlxModal } from "fmlx-common-ui";
import React, { useRef, useState } from "react";
import { downloadFile, showAlertSnackBar } from "utils/Global";
import './RMErrorModal.scss';
import { ResponseType } from "business/models/Models";
import MiscService from "business/services/MiscService";
import EAlertType from "enums/EAlertType";
import ESnackBarPosition from "enums/ESnackBarPosition";
import RMSvgIcon from "components/customComponents/CommonComponents/RMSvgIcon";
interface ErrorMessage {
  hasError: boolean;
  stackTrace: string;
  errorInfo: string;
  errorTime: Date;
  showClose: boolean;
  closeErrorModal?: () => void;
  isLoadingStackTrace?: boolean;
}

type LogIdentifier = "name" | "isodate"

const generateLogIdentifier = (type: LogIdentifier, date: Date): string => {
  const pad = (n: number) => String(n).padStart(2, '0');

  const day = pad(date.getDate());
  const month = pad(date.getMonth() + 1); // Months are 0-indexed
  const year = date.getFullYear();
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  const seconds = pad(date.getSeconds());
  const miliseconds = String(date.getMilliseconds()).padStart(3, '0');

  if (type === "name") {
    return `Error log ${day}-${month}-${year} ${hours}-${minutes}-${seconds}.txt`;
  }

  if (type === "isodate") {
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${miliseconds}`;
  }
};

type ActionType = "continue" | "reload" | "homepage";

const handleActions = (action: ActionType) => {
  if (action === "homepage") {
    window.location.assign('/');
  } else if (action === "reload") {
    window.location.reload();
  }
}

const RMErrorModal: React.FC<ErrorMessage> = ({
  hasError,
  errorInfo,
  stackTrace,
  errorTime,
  showClose,
  closeErrorModal,
  isLoadingStackTrace = false,
}) => {
  const showErrorModal = hasError;
  const [notify, setNotify] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const handleNextAction = async (action: ActionType) => {
    if (closeErrorModal) {
      closeErrorModal();
    }

    if (!notify) {
      handleActions(action);
      return;
    }

    try {
      setIsLoading(true);
      const miscService = new MiscService();
      const controller = new AbortController();
      abortControllerRef.current = controller;
      const res = await miscService.sendErrorEmail(errorInfo, stackTrace, controller.signal);
      if (res) {
        if (ResponseType[res.responseType] === 'Info') {
          showAlertSnackBar(EAlertType.SUCCESS, ESnackBarPosition.TOP, 'Email sent to Formulatrix successfully.');
        } else {
          showAlertSnackBar(EAlertType.ERROR, ESnackBarPosition.TOP, 'Failed to send email to Formulatrix.');
        }
      }
    } catch (err: any) {
      // Show snackbar error on request canceled
      if (err.name === 'AbortError') {
        showAlertSnackBar(EAlertType.ERROR, ESnackBarPosition.TOP, 'Email sending canceled by user.');
      }
    } finally {
      setIsLoading(false);
      setTimeout(() => {
        handleActions(action);
      }, 3000); // set timeout 3 seconds to give user read the snackbar alert
    }
  };

  const handleClick = (action: ActionType) => {
    handleNextAction(action);
  };

  const downloadLogClick = () => {
    // eslint-disable-next-line curly
    if (isLoadingStackTrace) return;

    const content = `${errorInfo}\n` +
      `Date: ${generateLogIdentifier("isodate", errorTime)}\n` +
      `Stack Trace:\n${stackTrace}`;

    downloadFile(new Blob([content], { type: 'text/plain' }), generateLogIdentifier("name", errorTime));
  };

  const handleCancelSendEmail = () => {
    abortControllerRef.current?.abort();
  };

  return (
    <>
      <FmlxModal
      id="rm-error-modal"
      data-testid="rm-error-modal"
      open={showErrorModal}
      hideCloseButton={!showClose || closeErrorModal === undefined}
      title="Something went wrong"
      size={FmlxModal.Size.MEDIUM}
      onCloseClick={closeErrorModal}
      primaryButton={{
        show: closeErrorModal ? true : false,
        disabled: notify && isLoadingStackTrace,
        label: "CONTINUE",
        onClick: () => handleClick("continue"),
        type: "primary",
        variant: "contain",
      }}
      secondaryButton={{
        show: true,
        disabled: notify && isLoadingStackTrace,
        label: "RELOAD",
        onClick: () => handleClick("reload"),
        type: "primary",
        variant: "contain",
      }}
      tertiaryButton={{
        show: true,
        disabled: notify && isLoadingStackTrace,
        label: "HOME PAGE",
        onClick: () => handleClick("homepage"),
        type: "basic",
        variant: "contain",
      }}
    >
      <>
        <div
          id="rm-error-modal-message"
          data-testid="rm-error-modal-message"
          className="text-sm text-slate-600"
        >
          An unexpected error has interrupted your session.
          Please try reloading or return to the home page.
          To help us investigate, please check the Notify Formulatrix checkbox
          or download the error logs to share with the support team.
        </div>
        <div
          id="rm-error-modal-notify"
          data-testid="rm-error-modal-notify"
          className="mt-3 flex justify-between items-center"
        >
          <FmlxCheckBox
            id="rm-error-modal-notify-checkbox"
            data-testid="rm-error-modal-notify-checkbox"
            label="Notify Formulatrix"
            disabled={isLoadingStackTrace}
            checked={notify}
            onChange={() => setNotify(!notify)}
          />
          <FmlxButton
            id="rm-error-modal-log-button"
            data-testid="rm-error-modal-log-button"
            label="LOG"
            withTooltip
            tooltipPlacement={FmlxButton.TooltipPlacement.BOTTOM}
            tooltipDescription="Download error logs"
            withIcon={FmlxButton.WithIcon.START}
            icon={<RMSvgIcon name="DownloadLog" />} // TODO: update this using FmlxIcon "FloDownloadIcon" with latest fmlx-common-ui
            showProgress={isLoadingStackTrace}
            onClick={downloadLogClick}
            type={FmlxButton.Type.BASIC}
            variant={FmlxButton.Variant.CONTAIN}
          />
        </div>
      </>
    </FmlxModal>
    <FmlxBackdropLoading
      id="rm-error-modal-backdrop-loading"
      show={isLoading}
      description="Sending email to notify Formulatrix..."
      size={FmlxBackdropLoading.Size.MEDIUM}
      showButton
      buttonLabel="CANCEL"
      onClick={handleCancelSendEmail}
    />
    </>
  );
};

export default RMErrorModal;